import { useState } from 'react';
import FishTask from '../components/FishTask';
import Loading from '../components/Loading';
import { joinClassroom } from '../lib/api';
import { dayInZone, shortDate, today } from '../lib/dates';
import { isDue } from '../lib/cards';
import { KIND, isOut } from '../lib/kinds';
import { lang } from '../lib/languages';
import { useClassroom } from '../state/Classroom';
import Dashboard from './teacher/Dashboard';
import MeetingCard from '../components/MeetingCard';

// ─────────────────────────────────────────────────────────────────────────────
// The student homescreen is a FEED of today's tasks, each block led by the fish
// whose art is about it (watch fish = video, story fish = story, anglerfish =
// grammar, yellow = flashcards, blue = vocabulary).
//
// Teachers get no fish: their home is the dashboard in teacher/Dashboard.tsx.
// ─────────────────────────────────────────────────────────────────────────────

export default function Home() {
  const { role, error, hydrated } = useClassroom();
  if (error) return <div className="notice bad">{error}</div>;
  if (!hydrated) return <Loading />;
  return role === 'student' ? <StudentHome /> : <Dashboard />;
}

// ── Student ─────────────────────────────────────────────────────────────────

/** The fish that stands for each kind of task — the one whose art is about it. */
const FISH = {
  video: 'watch_fish.riv',
  story: 'story_fish.riv',
  grammar: 'imports_fish.riv',
  cards: 'phrase_fish.riv',
  vocab: 'output_fish.riv',
} as const;

const KIND_ORDER = { video: 0, story: 1, grammar: 2 } as const;

type Task = {
  key: string; riv: string; to: string; label: string; title: string;
  detail?: string; chip?: string; progress?: number; overdue?: boolean;
};

/**
 * Today's feed. Every assignment whose week has started lands in one of:
 *   to do      not finished, and (if one-per-day) not already done today
 *   done today finished today, or today's one-per-day pass is in
 *   (hidden)   finished on an earlier day — it lives in All work
 * Anything left over from an earlier week, or past its due date, is overdue
 * and goes to the top. Flashcards join the list whenever cards are due.
 */
function StudentHome() {
  const { active, student, assignments, progressFor, passesFor, cards } = useClassroom();
  if (!active) return <JoinFirstClass />;

  const tz = active.timezone;
  const todayHere = dayInZone(new Date(), tz);
  const dueCards = cards.filter(c => isDue(c)).length;
  const reviewedToday = cards.some(c => c.last_reviewed_at && dayInZone(c.last_reviewed_at, tz) === todayHere);

  const todo: Task[] = [];
  const done: Task[] = [];

  for (const a of assignments) {
    if (!isOut(a, today())) continue;
    const p = progressFor(a.id);
    const n = p?.completions ?? 0;
    const finishedOn = p?.completed_at ? dayInZone(p.completed_at, tz) : null;
    const doneToday = a.one_per_day && passesFor(a.id).some(x => dayInZone(x.finished_at, tz) === todayHere);
    const overdue = !finishedOn && !!a.due_date && a.due_date < today();

    const base: Task = {
      key: a.id, riv: FISH[a.kind], to: `/a/${a.id}`, label: KIND[a.kind].label, title: a.title,
      chip: a.required_count > 1 ? `${Math.min(n, a.required_count)}/${a.required_count}` : undefined,
      progress: a.required_count > 1 ? n / a.required_count : undefined,
    };

    if (finishedOn) {
      if (finishedOn === todayHere) done.push({ ...base, detail: 'Finished today 🎉', chip: '✓' });
      continue;
    }
    if (doneToday) {
      done.push({ ...base, detail: `Today's ${KIND[a.kind].verb} is done. Come back tomorrow`, chip: '✓' });
      continue;
    }
    const left = a.required_count - n;
    const what = a.kind === 'grammar'
      ? `${left} lesson${left === 1 ? '' : 's'} to go`
      : n === 0
        ? `${a.kind === 'video' ? 'Watch' : 'Read'} ${a.required_count} times`
        : `${a.kind === 'video' ? 'Watch' : 'Read'} it ${left === 1 ? 'once more' : `${left} more times`}`;
    const when = a.due_date ? `due ${shortDate(a.due_date)}` : null;
    const pays = (a.sd_per_pass ?? 0) * Math.max(0, a.required_count - n) + (a.sd_on_complete ?? 0);
    todo.push({
      ...base, overdue: !!overdue,
      detail: [overdue ? 'Overdue' : null, a.required_count > 1 ? what : null, a.one_per_day && left > 1 ? 'one a day' : null, overdue ? null : when, pays > 0 ? `🪙 ${pays}` : null]
        .filter(Boolean).join(' · '),
    });
  }

  todo.sort((x, y) => Number(!!y.overdue) - Number(!!x.overdue)
    || KIND_ORDER[assignments.find(a => a.id === x.key)!.kind] - KIND_ORDER[assignments.find(a => a.id === y.key)!.kind]);

  if (dueCards > 0) {
    todo.push({
      key: 'cards', riv: FISH.cards, to: '/cards', label: 'Flashcards',
      title: `Review ${dueCards} flashcard${dueCards === 1 ? '' : 's'}`,
      detail: 'A few minutes a day is what makes words stick', chip: String(dueCards),
    });
  } else if (reviewedToday) {
    done.push({ key: 'cards', riv: FISH.cards, to: '/cards', label: 'Flashcards', title: 'Flashcards reviewed', detail: 'All caught up for today', chip: '✓' });
  }

  const total = todo.length + done.length;
  const first = student?.display_name.split(' ')[0];
  const dateLine = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="feed">
      <header className="feed-head">
        <div>
          <h1 dir="auto">{lang(active.language).hello}, {first}!</h1>
          <p className="sub">{dateLine} · {active.name}</p>
        </div>
        {total > 0 && (
          <div className="today-meter" aria-label={`${done.length} of ${total} done today`}>
            <span className="small muted">{todo.length === 0 ? 'All done today 🎉' : `${done.length} of ${total} done today`}</span>
            <span className="progress"><i style={{ width: `${(done.length / total) * 100}%` }} /></span>
          </div>
        )}
      </header>

      <MeetingCard c={active} />

      {todo.length > 0 ? (
        <section>
          <h2>To do today</h2>
          <div className="task-list">
            {todo.map(({ key, overdue, ...t }) => <FishTask key={key} {...t} tone={overdue ? 'overdue' : 'todo'} />)}
          </div>
        </section>
      ) : (
        <div className="notice good all-done">
          {total === 0
            ? 'Nothing assigned right now. Check back when your teacher posts new work.'
            : `${lang(active.language).praise[0]} You’re done for today. Come back tomorrow.`}
        </div>
      )}

      {done.length > 0 && (
        <section>
          <h2>Done today ✓</h2>
          <div className="task-list">
            {done.map(({ key, overdue: _o, progress: _p, ...t }) => <FishTask key={key} {...t} tone="done" size={72} />)}
          </div>
        </section>
      )}

      <section>
        <h2>More</h2>
        <div className="task-list">
          <FishTask riv={FISH.vocab} to="/vocab" label="Vocabulary" tone="quiet" size={72}
            title={cards.length ? `${cards.length} word${cards.length === 1 ? '' : 's'} saved` : 'Your saved words'}
            detail="Add words, fix translations, search" />
          {dueCards === 0 && !reviewedToday && (
            <FishTask riv={FISH.cards} to="/cards" label="Flashcards" tone="quiet" size={72}
              title={cards.length ? 'Nothing due right now' : 'No flashcards yet'}
              detail={cards.length ? 'Your next cards come up soon' : 'Tap words in a story and press ＋ Flashcard'} />
          )}
        </div>
      </section>
    </div>
  );
}

function JoinFirstClass() {
  return (
    <div className="narrow">
      <h1>No class yet</h1>
      <p className="sub">You're not in a class right now. Ask your teacher for the class code.</p>
      <JoinForm />
    </div>
  );
}

export function JoinForm() {
  const { refresh, setActiveId } = useClassroom();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const c = await joinClassroom(code);
      setActiveId(c.id);
      setCode('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel" onSubmit={join}>
      <div className="field">
        <label htmlFor="join">Class code</label>
        <input
          id="join" className="code-input" maxLength={6} required
          value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
        />
      </div>
      {error && <div className="notice bad small" style={{ marginBottom: 14 }}>{error}</div>}
      <button className="primary" disabled={busy || code.length !== 6}>{busy ? 'Joining…' : 'Join class'}</button>
    </form>
  );
}
